import { Browser, EventHandler } from '@syncfusion/ej2-base';
import { addClass, removeClass } from '@syncfusion/ej2-base';
import { formatUnit, isNullOrUndefined } from '@syncfusion/ej2-base';
import { getScrollBarWidth, getUpdateUsingRaf } from '../base/util';
import { scroll, contentReady, uiUpdate, onEmpty, headerRefreshed, textWrapRefresh, virtualScrollEdit, infiniteScrollHandler, closeFilterDialog } from '../base/constant';
import { lazyLoadScrollHandler, checkScrollReset, lastRowCellBorderUpdated } from '../base/constant';
import { ColumnWidthService } from '../services/width-controller';
import * as literals from '../base/string-literals';
import * as events from '../base/constant';
/**
 * The `Scroll` module is used to handle scrolling behaviour.
 */
export class Scroll {
    /**
     * Constructor for the Grid scrolling.
     *
     * @param {IGrid} parent - specifies the IGrid
     * @hidden
     */
    constructor(parent) {
        //To maintain scroll state on grid actions.
        this.previousValues = { top: 0, left: 0 };
        this.oneTimeReady = true;
        this.parent = parent;
        this.widthService = new ColumnWidthService(parent);
        this.addEventListener();
    }
    /**
     * For internal use only - Get the module name.
     *
     * @returns {string} returns the module name
     * @private
     */
    getModuleName() {
        return 'scroll';
    }
    /**
     * @param {boolean} uiupdate - specifies the uiupdate
     * @returns {void}
     * @hidden
     */
    setWidth(uiupdate) {
        this.parent.element.style.width = formatUnit(this.parent.width);
        if (uiupdate) {
            this.widthService.setWidthToColumns();
        }
        if (this.parent.toolbarModule && this.parent.toolbarModule.toolbar &&
            this.parent.toolbarModule.toolbar.element) {
            const tlbrElement = this.parent.toolbarModule.toolbar.element;
            const tlbrLeftElement = tlbrElement.querySelector('.e-toolbar-left');
            const tlbrCenterElement = tlbrElement.querySelector('.e-toolbar-center');
            const tlbrRightElement = tlbrElement.querySelector('.e-toolbar-right');
            const tlbrItems = tlbrElement.querySelector('.e-toolbar-items');
            const tlbrLeftWidth = tlbrLeftElement ? tlbrLeftElement.clientWidth : 0;
            const tlbrCenterWidth = tlbrCenterElement ? tlbrCenterElement.clientWidth : 0;
            const tlbrRightWidth = tlbrRightElement ? tlbrRightElement.clientWidth : 0;
            const tlbrItemsWidth = tlbrItems ? tlbrItems.clientWidth : 0;
            const tlbrWidth = tlbrElement ? tlbrElement.clientWidth : 0;
            if (!this.parent.enableAdaptiveUI || tlbrLeftWidth > tlbrWidth || tlbrCenterWidth > tlbrWidth || tlbrRightWidth > tlbrWidth ||
                tlbrItemsWidth > tlbrWidth) {
                this.parent.toolbarModule.toolbar.refreshOverflow();
            }
        }
    }
    /**
     * @returns {void}
     * @hidden
     */
    setHeight() {
        let mHdrHeight = 0;
        const content = this.parent.getContent().querySelector('.' + literals.content);
        let height = this.parent.height;
        if (this.parent.enableColumnVirtualization && this.parent.isFrozenGrid()) {
            if (this.parent.height !== 'auto' && this.parent.height.toString().indexOf('%') < 0) {
                height = parseInt(height, 10) - Scroll.getScrollBarWidth();
            }
            const movableScrollbarElement = this.parent.getContent().querySelector('.e-movablescrollbar');
            if (this.parent.height.toString().indexOf('%') > 0 && !isNullOrUndefined(movableScrollbarElement)) {
                height = 'calc(' + height + ' - ' + movableScrollbarElement.scrollHeight + 'px)';
            }
        }
        if (!this.parent.enableVirtualization && this.parent.frozenRows && this.parent.height !== 'auto' &&
            this.parent.height !== '100%') {
            const tbody = this.parent.getHeaderContent()
                .querySelector(literals.tbody + ':not(.e-masked-tbody)');
            mHdrHeight = tbody ? tbody.offsetHeight : 0;
            if (tbody && mHdrHeight) {
                const add = tbody.getElementsByClassName(literals.addedRow).length;
                const height = add * this.parent.getRowHeight();
                mHdrHeight -= height;
            }
            else if (!this.parent.isInitialLoad && this.parent.loadingIndicator.indicatorType === 'Shimmer'
                && this.parent.getHeaderContent().querySelector('.e-masked-table')) {
                height = parseInt(height, 10) - (this.parent.frozenRows * this.parent.getRowHeight());
            }
            content.style.height = formatUnit(parseInt(height, 10) - mHdrHeight);
        }
        else {
            content.style.height = formatUnit(height);
        }
        this.ensureOverflow(content);
        if (this.parent.isFrozenGrid()) {
            this.refresh();
        }
    }
    /**
     * @returns {void}
     * @hidden
     */
    setPadding() {
        const content = this.parent.getHeaderContent();
        const scrollWidth = Scroll.getScrollBarWidth() - this.getThreshold();
        const cssProps = this.getCssProperties();
        content.querySelector('.' + literals.headerContent).style[cssProps.border] = scrollWidth > 0 ? '1px' : '0px';
        content.style[cssProps.padding] = scrollWidth > 0 ? scrollWidth + 'px' : '0px';
    }
    /**
     * @param {boolean} rtl - specifies the rtl
     * @returns {void}
     * @hidden
     */
    removePadding(rtl) {
        const cssProps = this.getCssProperties(rtl);
        const hDiv = this.parent.getHeaderContent().querySelector('.' + literals.headerContent);
        hDiv.style[cssProps.border] = '';
        hDiv.parentElement.style[cssProps.padding] = '';
        const footerDiv = this.parent.getFooterContent();
        if (footerDiv && footerDiv.classList.contains('e-footerpadding')) {
            footerDiv.classList.remove('e-footerpadding');
        }
    }
    /**
     * Refresh makes the Grid adoptable with the height of parent container.
     *
     * > The [`height`](./#height) must be set to 100%.
     *
     * @returns {void}
     */
    refresh() {
        if (this.parent.height !== '100%') {
            return;
        }
        const content = this.parent.getContent();
        this.parent.element.style.height = '100%';
        const height = this.widthService.getSiblingsHeight(content);
        content.style.height = 'calc(100% - ' + height + 'px)'; //Set the height to the  '.' + literals.gridContent;
    }
    getThreshold() {
        /* Some browsers places the scroller outside the content,
         * hence the padding should be adjusted.*/
        const appName = Browser.info.name;
        if (appName === 'mozilla') {
            return 0.5;
        }
        return 1 + (1 - window.devicePixelRatio);
    }
    /**
     * @returns {void}
     * @hidden
     */
    addEventListener() {
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.on(onEmpty, this.wireEvents, this);
        this.parent.on(contentReady, this.wireEvents, this);
        this.parent.on(uiUpdate, this.onPropertyChanged, this);
        this.parent.on(textWrapRefresh, this.wireEvents, this);
        this.parent.on(headerRefreshed, this.setScrollLeft, this);
    }
    /**
     * @returns {void}
     * @hidden
     */
    removeEventListener() {
        if (this.parent.isDestroyed) {
            return;
        }
        this.parent.off(onEmpty, this.wireEvents);
        this.parent.off(contentReady, this.wireEvents);
        this.parent.off(uiUpdate, this.onPropertyChanged);
        this.parent.off(textWrapRefresh, this.wireEvents);
        this.parent.off(headerRefreshed, this.setScrollLeft);
        this.unwireEvents();
    }
    unwireEvents() {
        if (this.parent.frozenRows && this.header) {
            EventHandler.remove(this.header, 'touchstart pointerdown', this.setPageXY);
            EventHandler.remove(this.header, 'touchmove pointermove', this.onTouchScroll);
        }
        const mScrollBar = this.parent.getContent() ? this.parent.getContent().querySelector('.e-movablescrollbar') : null;
        if (this.parent.isFrozenGrid() && this.parent.enableColumnVirtualization) {
            if (mScrollBar) {
                EventHandler.remove(mScrollBar, 'scroll', this.onCustomScrollbarScroll);
            }
            if (this.content) {
                EventHandler.remove(this.content, 'scroll', this.onCustomScrollbarScroll);
                EventHandler.remove(this.content, 'touchstart pointerdown', this.setPageXY);
                if (!(/macintosh|ipad/.test(Browser.userAgent.toLowerCase()) && Browser.isDevice)) {
                    EventHandler.remove(this.content, 'touchmove pointermove', this.onTouchScroll);
                }
            }
            if (this.header) {
                EventHandler.remove(this.header, 'scroll', this.onCustomScrollbarScroll);
                EventHandler.remove(this.header, 'touchstart pointerdown', this.setPageXY);
                EventHandler.remove(this.header, 'touchmove pointermove', this.onTouchScroll);
            }
        }
        if (this.content) {
            EventHandler.remove(this.content, 'scroll', this.contentScrollHandler);
        }
        if (this.header) {
            EventHandler.remove(this.header, 'scroll', this.headerScrollHandler);
        }
        this.contentScrollHandler = null;
        this.headerScrollHandler = null;
        if (this.parent.aggregates.length && this.parent.getFooterContent()) {
            EventHandler.remove(this.parent.getFooterContent().firstChild, 'scroll', this.onContentScroll);
        }
    }
    setScrollLeft() {
        this.parent.getHeaderContent().querySelector('.' + literals.headerContent).scrollLeft = this.previousValues.left;
    }
    /**
     *
     * @param {HTMLElement} content - specifies the grid content element.
     * @returns {void}
     */
    updateFrozenShadow(content) {
        const gridContent = this.parent.getContent().firstElementChild;
        if (isNullOrUndefined(gridContent) && isNullOrUndefined(gridContent)) {
            return;
        }
        const target = content ? content : gridContent;
        const scrollLeft = this.parent.enableRtl ? Math.abs(target.scrollLeft) : target.scrollLeft;
        const isStartOfScroll = scrollLeft <= 1;
        if (this.parent.getVisibleFrozenLeftCount() && !isStartOfScroll) {
            addClass([this.parent.element], 'e-left-shadow');
        }
        else {
            removeClass([this.parent.element], 'e-left-shadow');
        }
        const scrollRight = this.parent.enableRtl ? Math.abs(target.scrollLeft) : target.scrollLeft;
        const isEndOfScroll = Math.round(scrollRight + target.clientWidth) >= target.scrollWidth - 1;
        if (this.parent.getVisibleFrozenRightCount()) {
            if (isEndOfScroll) {
                removeClass([this.parent.element], 'e-right-shadow');
            }
            else {
                addClass([this.parent.element], 'e-right-shadow');
            }
        }
        else {
            removeClass([this.parent.element], 'e-right-shadow');
        }
    }
    onContentScroll(scrollTarget) {
        const element = scrollTarget;
        const isHeader = element.classList.contains(literals.headerContent);
        return (e) => {
            if (this.content.querySelector(literals.tbody) === null || this.parent.isPreventScrollEvent) {
                return;
            }
            const target = e.target;
            if (this.parent.frozenRows) {
                if (this.content.scrollTop > 0 && this.parent.frozenRows) {
                    addClass([this.parent.element], 'e-top-shadow');
                }
                else {
                    removeClass([this.parent.element], 'e-top-shadow');
                }
            }
            if (this.parent.element.querySelectorAll('.e-leftfreeze,.e-fixedfreeze,.e-rightfreeze').length) {
                const errorFreeze = this.parent.getContent().querySelectorAll('.e-freezeerror:not([style*="display: none"])');
                const errorFixed = this.parent.getContent().querySelectorAll('.e-fixederror:not([style*="display: none"])');
                this.updateFrozenShadow(target);
                const rows = [].slice.call(this.parent.getContent().querySelectorAll('.e-row:not(.e-hiddenrow)'));
                if (((rows.length === 1 && errorFreeze.length) ||
                    (this.parent.element.querySelector('.e-freeze-autofill:not([style*="display: none"])')) ||
                    errorFixed.length) && target.scrollLeft !== this.previousValues.left) {
                    target.scrollLeft = this.previousValues.left;
                    return;
                }
                if (rows.length !== 1 && (errorFreeze.length || errorFixed.length) && target.scrollTop !== this.previousValues.top) {
                    target.scrollTop = this.previousValues.top;
                    return;
                }
            }
            const left = target.scrollLeft;
            if (!isNullOrUndefined(this.parent.infiniteScrollModule) && this.parent.enableInfiniteScrolling && (!this.parent.isEdit
                || (this.parent.editSettings.showAddNewRow && !this.parent.element.querySelector('.e-editedrow')))) {
                this.parent.notify(infiniteScrollHandler, { target: e.target, isLeft: this.previousValues.left !== left });
            }
            if (this.parent.groupSettings.columns.length && this.parent.groupSettings.enableLazyLoading) {
                const isDown = this.previousValues.top < this.parent.getContent().firstElementChild.scrollTop;
                this.parent.notify(lazyLoadScrollHandler, { scrollDown: isDown });
            }
            this.parent.notify(virtualScrollEdit, {});
            const isFooter = target.classList.contains('e-summarycontent');
            if (this.previousValues.left === left) {
                this.previousValues.top = !isHeader ? this.previousValues.top : target.scrollTop;
                return;
            }
            this.parent.notify(closeFilterDialog, e);
            element.scrollLeft = left;
            if (isFooter) {
                this.header.scrollLeft = left;
            }
            this.previousValues.left = left;
            this.parent.notify(scroll, { left: left });
        };
    }
    onCustomScrollbarScroll(cont, hdr) {
        const content = cont;
        const header = hdr;
        return (e) => {
            if (this.content.querySelector(literals.tbody) === null) {
                return;
            }
            const target = e.target;
            const left = target.scrollLeft;
            if (this.previousValues.left === left) {
                return;
            }
            content.scrollLeft = left;
            header.scrollLeft = left;
            this.previousValues.left = left;
            this.parent.notify(scroll, { left: left });
            if (this.parent.isDestroyed) {
                return;
            }
        };
    }
    onTouchScroll(scrollTarget) {
        const element = scrollTarget;
        return (e) => {
            if (e.pointerType === 'mouse') {
                return;
            }
            const isFrozen = this.parent.isFrozenGrid();
            const pageXY = this.getPointXY(e);
            const left = element.scrollLeft + (this.pageXY.x - pageXY.x);
            const mHdr = this.parent.getHeaderContent().querySelector('.' + literals.headerContent);
            const mCont = this.parent.getContent().querySelector('.' + literals.content);
            if (this.previousValues.left === left || (left < 0 || (mHdr.scrollWidth - mHdr.clientWidth) < left)) {
                return;
            }
            e.preventDefault();
            mHdr.scrollLeft = left;
            mCont.scrollLeft = left;
            if (isFrozen && this.parent.enableColumnVirtualization) {
                const scrollBar = this.parent.getContent().querySelector('.e-movablescrollbar');
                scrollBar.scrollLeft = left;
            }
            this.pageXY.x = pageXY.x;
            this.previousValues.left = left;
        };
    }
    setPageXY() {
        return (e) => {
            if (e.pointerType === 'mouse') {
                return;
            }
            this.pageXY = this.getPointXY(e);
        };
    }
    getPointXY(e) {
        const pageXY = { x: 0, y: 0 };
        if (e.touches && e.touches.length) {
            pageXY.x = e.touches[0].pageX;
            pageXY.y = e.touches[0].pageY;
        }
        else {
            pageXY.x = e.pageX;
            pageXY.y = e.pageY;
        }
        return pageXY;
    }
    getScrollbleParent(node) {
        if (node === null) {
            return null;
        }
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const parent = isNullOrUndefined(node.tagName) ? node.scrollingElement : node;
        const overflowY = document.defaultView.getComputedStyle(parent, null).overflowY;
        if (parent.scrollHeight > parent.clientHeight && overflowY !== 'hidden'
            && overflowY !== 'visible' || node.tagName === 'HTML' || node.tagName === 'BODY') {
            return node;
        }
        else {
            return this.getScrollbleParent(node.parentNode);
        }
    }
    /**
     * @param {boolean} isAdd - specifies whether adding/removing the event
     * @returns {void}
     * @hidden
     */
    addStickyListener(isAdd) {
        this.parentElement = this.getScrollbleParent(this.parent.element.parentElement);
        if (isAdd && this.parentElement) {
            this.eventElement = this.parentElement.tagName === 'HTML' || this.parentElement.tagName === 'BODY' ? document :
                this.parentElement;
            EventHandler.add(this.eventElement, 'scroll', this.makeStickyHeader, this);
        }
        else if (this.eventElement) {
            EventHandler.remove(this.eventElement, 'scroll', this.makeStickyHeader);
            this.eventElement = null;
        }
    }
    /**
     * @returns {void}
     * @hidden
     */
    resizeFrozenRowBorder() {
        let div;
        if (!this.parent.element.querySelector('.e-frozenrow-border')) {
            div = this.parent.createElement('div', { className: 'e-frozenrow-border' });
            this.parent.element.insertBefore(div, this.parent.element.querySelector('.e-gridcontent'));
        }
        else {
            div = this.parent.element.querySelector('.e-frozenrow-border');
        }
        const scrollWidth = this.parent.height !== 'auto' ? Scroll.getScrollBarWidth() : 0;
        div.style.width = (this.parent.element.offsetWidth - scrollWidth) - 0.5 + 'px';
    }
    wireEvents() {
        if (this.oneTimeReady) {
            const frzCols = this.parent.isFrozenGrid();
            this.content = this.parent.getContent().querySelector('.' + literals.content);
            this.header = this.parent.getHeaderContent().querySelector('.' + literals.headerContent);
            const mScrollBar = this.parent.getContent().querySelector('.e-movablescrollbar');
            if ((this.parent.frozenRows || this.parent.isRowPinned) && this.header && this.content) {
                EventHandler.add(this.header, 'touchstart pointerdown', this.setPageXY(), this);
                EventHandler.add(this.header, 'touchmove pointermove', this.onTouchScroll(this.content), this);
            }
            if (frzCols && mScrollBar && this.parent.enableColumnVirtualization) {
                EventHandler.add(mScrollBar, 'scroll', this.onCustomScrollbarScroll(this.content, this.header), this);
                EventHandler.add(this.content, 'scroll', this.onCustomScrollbarScroll(mScrollBar, this.header), this);
                EventHandler.add(this.header, 'scroll', this.onCustomScrollbarScroll(mScrollBar, this.content), this);
                EventHandler.add(this.header, 'touchstart pointerdown', this.setPageXY(), this);
                EventHandler.add(this.header, 'touchmove pointermove', this.onTouchScroll(this.content), this);
                EventHandler.add(this.content, 'touchstart pointerdown', this.setPageXY(), this);
                if (!(/macintosh|ipad/.test(Browser.userAgent.toLowerCase()) && Browser.isDevice)) {
                    EventHandler.add(this.content, 'touchmove pointermove', this.onTouchScroll(this.header), this);
                }
            }
            this.contentScrollHandler = this.onContentScroll(this.header);
            this.headerScrollHandler = this.onContentScroll(this.content);
            EventHandler.add(this.content, 'scroll', this.contentScrollHandler, this);
            EventHandler.add(this.header, 'scroll', this.headerScrollHandler, this);
            if (this.parent.aggregates.length) {
                EventHandler.add(this.parent.getFooterContent().firstChild, 'scroll', this.onContentScroll(this.content), this);
            }
            if (this.parent.enableStickyHeader) {
                this.addStickyListener(true);
            }
            this.refresh();
            this.oneTimeReady = false;
        }
        const table = this.parent.getContentTable();
        let sLeft;
        let sHeight;
        let clientHeight;
        getUpdateUsingRaf(() => {
            sLeft = this.header.scrollLeft;
            sHeight = table.scrollHeight;
            clientHeight = this.parent.getContent().clientHeight;
        }, () => {
            const args = { cancel: false };
            this.parent.notify(checkScrollReset, args);
            if (sHeight < clientHeight && this.parent.height !== 'auto') {
                this.setLastRowCell();
                this.parent.notify(lastRowCellBorderUpdated, args);
            }
            if (this.parent.frozenRows || this.parent.pinnedTopRowModels.length) {
                this.resizeFrozenRowBorder();
            }
            if (this.parent.element.querySelector('.e-frozenrow-border')) {
                if ((this.parent.pinnedTopRowModels.length || this.parent.frozenRows) && this.parent.currentViewData.length) {
                    this.parent.element.querySelector('.e-frozenrow-border').classList.remove('e-frozenrow-empty');
                }
                else {
                    this.parent.element.querySelector('.e-frozenrow-border').classList.add('e-frozenrow-empty');
                }
            }
            if (!this.parent.enableVirtualization && !this.parent.enableInfiniteScrolling) {
                if (!args.cancel) {
                    this.header.scrollLeft = this.previousValues.left;
                    this.content.scrollLeft = this.previousValues.left;
                    this.content.scrollTop = this.previousValues.top;
                }
            }
            if (!this.parent.enableColumnVirtualization) {
                this.content.scrollLeft = sLeft;
                if (this.parent.isFrozenGrid()) {
                    this.previousValues.left = sLeft;
                }
            }
        });
        this.parent.isPreventScrollEvent = false;
    }
    /**
     * @returns {void} returns void
     * @hidden
     */
    setLastRowCell() {
        const table = this.parent.getContentTable();
        removeClass(table.querySelectorAll('td'), 'e-lastrowcell');
        if (table.querySelector('tr:nth-last-child(2)')) {
            if (this.parent.editSettings.showAddNewRow && this.parent.editSettings.newRowPosition === 'Bottom') {
                addClass(table.querySelector('tr:nth-last-child(2)').querySelectorAll('td'), 'e-lastrowcell');
            }
        }
        addClass(table.querySelectorAll('tr:last-child td'), 'e-lastrowcell');
        if (this.parent.isSpan) {
            addClass(table.querySelectorAll('.e-row-span-lastrowcell'), 'e-lastrowcell');
        }
    }
    /**
     * @param {boolean} rtl - specifies the rtl
     * @returns {ScrollCss} returns the ScrollCss
     * @hidden
     */
    getCssProperties(rtl) {
        const css = {};
        const enableRtl = isNullOrUndefined(rtl) ? this.parent.enableRtl : rtl;
        css.border = enableRtl ? 'borderLeftWidth' : 'borderRightWidth';
        css.padding = enableRtl ? 'paddingLeft' : 'paddingRight';
        return css;
    }
    ensureOverflow(content) {
        content.style.overflowY = this.parent.height === 'auto' ? 'auto' : 'scroll';
    }
    onPropertyChanged(e) {
        if (e.module !== this.getModuleName()) {
            return;
        }
        this.setPadding();
        this.oneTimeReady = true;
        if (this.parent.height === 'auto') {
            this.removePadding();
        }
        this.wireEvents();
        this.setHeight();
        const width = 'width';
        this.setWidth(!isNullOrUndefined(e.properties[`${width}`]));
    }
    /**
     * @returns {void}
     * @hidden
     */
    makeStickyHeader() {
        if (this.parent.enableStickyHeader && this.parent.element && this.parent.getContent()) {
            const contentRect = this.parent.getContent().getClientRects()[0];
            if (contentRect) {
                const windowScale = window.devicePixelRatio;
                const headerEle = this.parent.getHeaderContent();
                const toolbarEle = this.parent.element.querySelector('.e-toolbar');
                const groupHeaderEle = this.parent.element.querySelector('.e-groupdroparea');
                const height = headerEle.offsetHeight + (toolbarEle ? toolbarEle.offsetHeight : 0) +
                    (groupHeaderEle ? groupHeaderEle.offsetHeight : 0);
                const parentTop = this.parentElement.getClientRects()[0].top;
                let top = contentRect.top - (parentTop < 0 ? 0 : parentTop);
                const left = contentRect.left;
                const colMenuEle = document.body.querySelector('#' + this.parent.element.id + '_columnmenu');
                if (windowScale !== 1) {
                    top = Math.ceil(top);
                }
                if (top < height && contentRect.bottom > 0) {
                    headerEle.classList.add('e-sticky');
                    let elemTop = 0;
                    if (groupHeaderEle && this.parent.groupSettings.showDropArea) {
                        this.setSticky(groupHeaderEle, elemTop, contentRect.width, left, true);
                        elemTop += groupHeaderEle.getClientRects()[0].height;
                    }
                    if (toolbarEle) {
                        this.setSticky(toolbarEle, elemTop, contentRect.width, left, true);
                        elemTop += toolbarEle.getClientRects()[0].height;
                    }
                    this.setSticky(headerEle, elemTop, contentRect.width, left, true);
                    if (!isNullOrUndefined(colMenuEle)) {
                        colMenuEle.style.position = 'fixed';
                        colMenuEle.style.top = height + 'px';
                    }
                }
                else {
                    if (headerEle.classList.contains('e-sticky')) {
                        this.setSticky(headerEle, null, null, null, false);
                        if (toolbarEle) {
                            this.setSticky(toolbarEle, null, null, null, false);
                        }
                        if (groupHeaderEle) {
                            this.setSticky(groupHeaderEle, null, null, null, false);
                        }
                        const ccDlg = this.parent.element.querySelector('.e-ccdlg');
                        if (ccDlg) {
                            ccDlg.classList.remove('e-sticky');
                        }
                        if (!isNullOrUndefined(colMenuEle)) {
                            colMenuEle.style.position = 'absolute';
                            const topStyle = contentRect.top - parentTop;
                            colMenuEle.style.top = topStyle + 'px';
                        }
                    }
                }
                this.parent.notify(events.stickyScrollComplete, {});
            }
        }
    }
    setSticky(ele, top, width, left, isAdd) {
        if (isAdd) {
            ele.classList.add('e-sticky');
        }
        else {
            ele.classList.remove('e-sticky');
        }
        ele.style.width = width != null ? width + 'px' : '';
        ele.style.top = top != null ? top + 'px' : '';
        ele.style.left = left !== null ? parseInt(ele.style.left, 10) !== left ? left + 'px' : ele.style.left : '';
    }
    /**
     * @returns {void}
     * @hidden
     */
    destroy() {
        const gridElement = this.parent.element;
        if (!gridElement || (!gridElement.querySelector('.' + literals.gridHeader) && !gridElement.querySelector('.' + literals.gridContent))) {
            return;
        }
        this.removeEventListener();
        //Remove Dom event
        const cont = this.parent.getContent().querySelector('.' + literals.content);
        EventHandler.remove(cont, 'scroll', this.onContentScroll);
        if (this.parent.enableStickyHeader) {
            this.addStickyListener(false);
        }
        //Remove padding
        this.removePadding();
        removeClass([this.parent.getHeaderContent().querySelector('.' + literals.headerContent)], literals.headerContent);
        removeClass([cont], literals.content);
        //Remove height
        cont.style.height = '';
        //Remove width
        this.parent.element.style.width = '';
    }
    /**
     * Function to get the scrollbar width of the browser.
     *
     * @returns {number} return the width
     * @hidden
     */
    static getScrollBarWidth() {
        return getScrollBarWidth();
    }
}
